"use client";

import { Separator } from "@/components/ui/separator";
import { ClassType } from "@/lib/tailwind/properties";
import { cn } from "@/lib/utils";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ComboBox,
  ComboBoxOptionType,
  DraggableInput,
  DropdownInput,
  DropdownOptionType,
  TooltipProps,
} from ".";
import { GradientState, GradientInputState } from "../../hooks/useGradient";
import { hexToRgba, rgbaToHex } from "./color-picker/utils/convert";
import CustomColorPicker from "./CustomColorPicker";
import { ChevronDown } from "lucide-react";
import { ResetIcon } from "@radix-ui/react-icons";

type ColorString = string;

interface CustomGradientInputProps {
  backgroundImage: string;
  setBackgroundImage: (backgroundImage: string) => void;
  backgroundImageSet: boolean;
  setBackgroundImageSet: (set: boolean) => void;
  backgroundImageProperties: ClassType[];
  backgroundColorProperties: ClassType[];
  gradientDirectionIcon: React.JSX.Element;
  addTailwindClass: (option: ComboBoxOptionType) => void;
  removeTailwindClass: (className: string) => void;
  classList: string[];
  onValueUpdate?: (option: ComboBoxOptionType) => void;
  fromColorProperties?: ComboBoxOptionType[];
  toColorProperties?: ComboBoxOptionType[];
  viaColorProperties?: ComboBoxOptionType[];
  fromPositionProperties: ClassType[];
  toPositionProperties: ClassType[];
  viaPositionProperties: ClassType[];
  set: boolean;
  setSet: (set: boolean) => void;
  onClear?: () => void;
  evaluatedValue?: string;
  gradient: GradientState;
  setGradient: (val: React.SetStateAction<GradientState>) => void;
  buildArbitraryClass: (
    currentColor: "from" | "to" | "via",
    input: string
  ) => ComboBoxOptionType;
}

const CustomGradientInput: React.FC<
  CustomGradientInputProps & TooltipProps
> = ({
  backgroundImage,
  setBackgroundImage,
  backgroundImageSet,
  setBackgroundImageSet,
  backgroundImageProperties,
  gradientDirectionIcon,
  fromColorProperties,
  toColorProperties,
  viaColorProperties,
  onValueUpdate,
  fromPositionProperties,
  toPositionProperties,
  viaPositionProperties,
  removeTailwindClass,
  backgroundColorProperties,
  classList,
  set,
  addTailwindClass,
  onClear,
  gradient,
  setGradient,
  buildArbitraryClass,
}) => {
  const [localGradient, setLocalGradient] = useState<GradientState>(gradient);
  const [currentColor, setCurrentColor] = useState<"from" | "to" | "via">(
    "from"
  );

  const fromColorValue = useMemo(() => {
    if (
      localGradient.from &&
      localGradient.from.color &&
      localGradient.from.color.value
    ) {
      if (localGradient.from.color.value.startsWith("#")) {
        return localGradient.from.color.value
          .replace("var(--tw-gradient-from-position)", "")
          .trim();
      }

      // is color value rgba
      const rgbaValue = localGradient.from.color.value
        .replace("rgba(", "")
        .replace("rgb(", "")
        .replace(")", "")
        .replace(/,/g, "")
        .replace("var(--tw-gradient-from-position)", "")
        .trim()
        .split(" ");

      return rgbaToHex({
        r: Number(rgbaValue[0]),
        g: Number(rgbaValue[1]),
        b: Number(rgbaValue[2]),
        a: Number(rgbaValue[3]) || 1,
      });
    }

    return "#ffffff";
  }, [gradient, localGradient, currentColor]);

  const toColorValue = useMemo(() => {
    if (
      localGradient.to &&
      localGradient.to.color &&
      localGradient.to.color.value
    ) {
      if (localGradient.to.color.value.startsWith("#")) {
        return localGradient.to.color.value
          .replace("var(--tw-gradient-to-position)", "")
          .trim();
      }

      // is color value rgba
      const rgbaValue = localGradient.to.color.value
        .replace("rgba(", "")
        .replace("rgb(", "")
        .replace(")", "")
        .replace(/,/g, "")
        .replace("var(--tw-gradient-to-position)", "")
        .trim()
        .split(" ");

      return rgbaToHex({
        r: Number(rgbaValue[0]),
        g: Number(rgbaValue[1]),
        b: Number(rgbaValue[2]),
        a: Number(rgbaValue[3]) || 1,
      });
    }

    return "#000000";
  }, [gradient, localGradient, currentColor]);

  const viaColorValue = useMemo(() => {
    if (
      localGradient.via &&
      localGradient.via.color &&
      localGradient.via.color.value
    ) {
      if (localGradient.via.color.value.startsWith("#")) {
        return localGradient.via.color.value
          .replace("var(--tw-gradient-via-position)", "")
          .trim();
      }

      // is color value rgba
      const rgbaValue = localGradient.via.color.value
        .replace("rgba(", "")
        .replace("rgb(", "")
        .replace(")", "")
        .replace("var(--tw-gradient-via-position)", "")
        .replace(/,/g, "")
        .trim()
        .split(" ");

      return rgbaToHex({
        r: Number(rgbaValue[0]),
        g: Number(rgbaValue[1]),
        b: Number(rgbaValue[2]),
        a: Number(rgbaValue[3]) || 1,
      });
    }

    return "#000000";
  }, [gradient, localGradient, currentColor]);

  const currentColorValue = useMemo(() => {
    if (currentColor === "from") {
      return fromColorValue;
    } else if (currentColor === "to") {
      return toColorValue;
    } else if (currentColor === "via") {
      return viaColorValue;
    }
  }, [gradient, localGradient, currentColor]);

  const handleUpdateGradientSet = useCallback(
    (value: GradientState) => {
      setLocalGradient(value);
      setGradient(value);
    },
    [gradient, localGradient]
  );

  useEffect(() => {
    // at initial load, localGradient/localGradient.direction is null due to network latency,
    // update localGradient to gradient value
    if (localGradient == null || localGradient.direction == null) {
      setLocalGradient(gradient);
    }
  }, [gradient]);

  return (
    <div className="col-span-14">
      <div className="grid grid-cols-14 items-center gap-x-3 gap-y-2">
        <div className="col-span-14">
          <CustomColorPicker
            showHex={false}
            // @ts-expect-error TS2322
            value={currentColorValue}
            evaluatedValue={currentColorValue}
            setValue={(value) => {
              let colorSet = {};

              switch (currentColor) {
                case "from":
                  colorSet = {
                    from: {
                      color: {
                        value: value,
                        classname: `from-[${value}]`,
                      },
                      set: true,
                    },
                  };
                  break;
                case "to":
                  colorSet = {
                    to: {
                      color: {
                        value: value,
                        classname: `to-[${value}]`,
                      },
                      set: true,
                    },
                  };
                  break;
                case "via":
                  colorSet = {
                    via: {
                      color: {
                        value: value,
                        classname: `via-[${value}]`,
                      },
                      set: true,
                    },
                  };
                  break;
              }

              handleUpdateGradientSet({
                ...localGradient,
                ...colorSet,
              });
            }}
            buildArbitraryClass={(input: string) => {
              return buildArbitraryClass(currentColor, input);
            }}
            set={set}
            setSet={(value) => {
              let newValue = {
                ...localGradient,
                [currentColor]: {
                  ...localGradient[currentColor],
                  set: value,
                },
              };

              handleUpdateGradientSet(newValue);
            }}
            onValueUpdate={(option) => {
              onValueUpdate?.(option);
            }}
            comboboxOptions={backgroundColorProperties}
            onComboboxSelected={(option) => addTailwindClass(option)}
            tooltipContent="Background color"
            onClear={onClear}
          />
        </div>

        <div className="col-span-5">
          <div className="text-xs text-secondary-800 font-medium">
            Direction
          </div>
        </div>
        <div className="col-span-9">
          <DraggableInput
            value={backgroundImage}
            evaluatedValue={backgroundImage}
            labelContent={gradientDirectionIcon}
            setValue={setBackgroundImage}
            buildArbitraryClass={(input: string) => {
              return {
                tailwind: `bg-[${input}]`,
                css: `background-image: ${input}`,
              };
            }}
            set={backgroundImageSet}
            setSet={setBackgroundImageSet}
            comboboxOptions={backgroundImageProperties}
            onComboboxSelected={(option) => addTailwindClass(option)}
            tooltipContent="Background image"
            onClear={() => {
              const imageClass = classList.find((c) =>
                c.startsWith("bg-[url(")
              );
              const gradientClass = classList.find((c) =>
                c.startsWith("bg-gradient-")
              );

              const toRemove = imageClass || gradientClass;

              if (toRemove) {
                removeTailwindClass(toRemove);
              }
            }}
          />
        </div>
        <div className="col-span-14">
          <Separator className="bg-secondary-n-900" />
        </div>
        <GradientInputMemo
          title="From"
          // @ts-expect-error TS2322
          value={localGradient.from}
          addTailwindClass={addTailwindClass}
          onValueUpdate={onValueUpdate}
          colorProperties={fromColorProperties}
          removeTailwindClass={removeTailwindClass}
          buildArbitraryClass={(input) => buildArbitraryClass("from", input)}
          setCurrentColor={() => setCurrentColor("from")}
          setGradient={handleUpdateGradientSet}
          positionProperties={fromPositionProperties}
          dropdownOptions={[]}
        />
        <div className="col-span-14">
          <Separator className="bg-secondary-n-900" />
        </div>
        <GradientInputMemo
          title="Via"
          // @ts-expect-error TS2322
          value={localGradient.via}
          addTailwindClass={addTailwindClass}
          onValueUpdate={onValueUpdate}
          colorProperties={viaColorProperties}
          removeTailwindClass={removeTailwindClass}
          buildArbitraryClass={(input) => buildArbitraryClass("via", input)}
          setCurrentColor={() => setCurrentColor("via")}
          setGradient={handleUpdateGradientSet}
          positionProperties={viaPositionProperties}
          dropdownOptions={[]}
        />
        <div className="col-span-14">
          <Separator className="bg-secondary-n-900" />
        </div>
        <GradientInputMemo
          title="To"
          // @ts-expect-error TS2322
          value={localGradient.to}
          addTailwindClass={addTailwindClass}
          onValueUpdate={onValueUpdate}
          colorProperties={toColorProperties}
          removeTailwindClass={removeTailwindClass}
          buildArbitraryClass={(input) => buildArbitraryClass("to", input)}
          setCurrentColor={() => setCurrentColor("to")}
          setGradient={handleUpdateGradientSet}
          dropdownOptions={[]}
          positionProperties={toPositionProperties}
        />
      </div>
    </div>
  );
};

const GradientInput = ({
  title,
  value,
  onValueUpdate,
  colorProperties,
  addTailwindClass,
  removeTailwindClass,
  buildArbitraryClass,
  disabled = false,
  onInputFocus,
  dropdownOptions,
  setCurrentColor,
  setGradient,
  onDropdownSelected,
  positionProperties,
}: {
  title: string;
  value: GradientInputState;
  onValueUpdate?: (option: ComboBoxOptionType) => void;
  addTailwindClass: (option: ComboBoxOptionType) => void;
  colorProperties?: ComboBoxOptionType[];
  removeTailwindClass: (className: string) => void;
  buildArbitraryClass?: (input: string) => ComboBoxOptionType;
  disabled?: boolean;
  onInputFocus?: (event: React.FocusEvent<HTMLInputElement, Element>) => void;
  setCurrentColor: () => void;
  dropdownOptions: DropdownOptionType[];
  setGradient: (val: React.SetStateAction<GradientState>) => void;
  onDropdownSelected?: (option: DropdownOptionType) => void;
  positionProperties: ClassType[];
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [comboboxOpen, setComboboxOpen] = useState<null | boolean>(false);
  const [focused, setFocused] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState<null | boolean>(false);
  const [hovered, setHovered] = useState(false);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [positionRange, setPositionRange] = useState<number | undefined>(
    undefined
  );
  const [colorValue, setColorValue] = useState<string | undefined>(
    value?.color?.value
  );

  const getColorValue = useMemo(() => {
    if (value?.color?.value != colorValue) {
      return value?.color?.value;
    }

    if (colorValue) {
      return colorValue;
    }

    return "";
  }, [colorValue, value]);

  const positionValueForInput = useMemo(() => {
    if (positionRange != undefined) {
      return positionRange;
    }

    if (value?.position?.value != undefined) {
      return Number(value?.position?.value.replace("%", ""));
    }

    return 0;
  }, [value, positionRange]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter") {
      return;
    }

    if (value.color.value === "" && removeTailwindClass) {
      setComboboxOpen(false);
      // @ts-expect-error TS2345
      removeTailwindClass(value?.color?.classname);

      if (inputRef.current) {
        inputRef.current.blur();
      }
    }
  };

  const handleDropdownOptionClick = (option: DropdownOptionType) => {
    if (option.value === "clear" && removeTailwindClass) {
      // @ts-expect-error TS2345
      removeTailwindClass(value?.color?.classname);
    } else if (onDropdownSelected) {
      onDropdownSelected(option);
    }

    setDropdownVisible(false);
  };

  const enhancedDropdownOptions = useMemo(() => {
    // @ts-expect-error TS2774
    if (removeTailwindClass && value?.set == true) {
      const clearDropdownOption: DropdownOptionType = {
        label: "Clear style",
        value: "clear",
        icon: <ResetIcon />,
      };

      // Return a new array with the 'Clear' option appended
      return [...dropdownOptions, clearDropdownOption];
    }

    return dropdownOptions;
  }, [dropdownOptions, value, removeTailwindClass]);

  const handleComboboxOptionClick = useCallback(
    (option: ComboBoxOptionType) => {
      const key = title.toLowerCase();

      if (addTailwindClass) {
        addTailwindClass(option);
      }

      setComboboxOpen(false);

      const value = safeExtractValueFromCss(option.css);

      if (value) {
        setColorValue(value);

        setGradient((prev) => {
          return {
            ...prev,
            [key]: {
              color: {
                value: value,
                classname: `${key}-[${value}]`,
              },
              set: true,
            },
          };
        });
      }

      if (inputRef.current) {
        inputRef.current.blur();
      }
    },
    [value]
  );

  const safeExtractValueFromCss = (css: string): string | null => {
    try {
      const firstCss = css.split("\n")[0];
      let value: string = firstCss.split(":")[1];
      value = value.split(";")[0];

      return value.trim();
    } catch (error) {
      return null;
    }
  };

  const onInputChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const input = ev.target.value || "";
      const key = title.toLowerCase();

      if (input.length > 0) {
        setColorValue(input);
        setGradient((prev) => {
          return {
            ...prev,
            [key]: {
              color: {
                value: input,
                classname: `${key}-[${input}]`,
              },
              set: true,
            },
          };
        });
      }
      setComboboxOpen(true);
    },
    [value]
  );

  const handlePositionRangeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const position = Math.round(parseFloat(e.target.value) * 100);

      setPositionRange(position);
      if (isDragging) {
        const value = `${position}%`;

        const key = title.toLowerCase();
        setGradient((prev) => {
          return {
            ...prev,
            key: {
              ...prev[key],
              position: {
                value: value,
                classname: `to-[${value}]`,
              },
              set: true,
            },
          };
        });

        onValueUpdate?.(buildPositionArbitraryClass(key, value));
      }
    },
    [isDragging]
  );

  const handlePositionRangeMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handlePositionRangeMouseUp = useCallback(() => {
    setIsDragging(false);
    const key = title.toLowerCase();
    addTailwindClass(
      buildPositionArbitraryClass(key, `${positionValueForInput.toString()}%`)
    );
  }, [positionValueForInput]);

  const buildPositionArbitraryClass = (key: string, input: string) => {
    let css = `--tw-gradient-from-position: ${input}`;
    let tailwind = `from-[${input}]`;

    if (key === "to") {
      css = `--tw-gradient-to-position: ${input}`;
      tailwind = `to-[${input}]`;
    }

    if (key === "via") {
      css = `--tw-gradient-via-position: ${input}`;
      tailwind = `via-[${input}]`;
    }

    return {
      tailwind: tailwind,
      css: css,
    };
  };

  const extractFirstColor = (gradientString: string): ColorString => {
    // Match hex colors (3, 4, 6, or 8 digits)
    const hexPattern = /#([0-9A-Fa-f]{3,4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})\b/;

    // Match rgb/rgba patterns
    const rgbPattern =
      /rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(?:,\s*(?:0?\.)?\d+\s*)?\)|rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*(?:0?\.)?\d+\s*\)/;

    // Try to match hex first
    const hexMatch = gradientString.match(hexPattern);
    if (hexMatch) {
      return hexMatch[0];
    }

    // Try to match rgb/rgba
    const rgbMatch = gradientString.match(rgbPattern);
    if (rgbMatch) {
      return rgbMatch[0];
    }

    // Return a default color if no match is found
    return "#000000";
  };

  const buildColorPreview = (option: ComboBoxOptionType): ReactNode => {
    const styles = extractFirstColor(option.css);
    return (
      <div
        className={`w-full h-full`}
        style={{
          backgroundColor: styles,
        }}
      ></div>
    );
  };

  return (
    <div className="col-span-14">
      <div className="grid grid-cols-14 items-start">
        <div className="col-span-5">
          <div className="text-xs text-secondary-800 font-medium">{title}</div>
        </div>
        <div className="col-span-9">
          <div className="grid grid-cols-10 gap-1 items-center">
            <div className="col-span-10">
              <div ref={colorInputRef}>
                {/*
                 // @ts-expect-error TS2322 */}
                <div
                  className="flex justify-between items-center mb-2"
                  data-tooltip-id="styling-panel-tooltip"
                  data-tooltip-content={`${title} color`}
                  data-tooltip-place="bottom"
                  data-tooltip-offset={5}
                  data-tooltip-hidden={comboboxOpen || dropdownVisible}
                  onMouseEnter={() => setHovered(true)}
                  onMouseLeave={() => setHovered(false)}
                  onMouseDown={(event) => {
                    if (event.button === 2) {
                      // Right click
                      event.preventDefault();
                    }
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    if (
                      enhancedDropdownOptions &&
                      enhancedDropdownOptions.length > 0
                    ) {
                      setDropdownVisible(true);
                    }
                  }}
                >
                  <div
                    className={cn(
                      "flex items-center gap-1 w-full max-w-[140px] h-7 bg-secondary-n-950 rounded-md relative",
                      focused && "border border-primary-400"
                    )}
                  >
                    <button
                      type="button"
                      onClick={setCurrentColor}
                      className="ml-1 w-3 h-3 rounded-xs shadow-inner cursor-pointer absolute"
                      style={{
                        backgroundColor: getColorValue
                          .replace("var(--tw-gradient-to-position)", "")
                          .replace("/ 0)", "/ 1)"),
                      }}
                    ></button>
                    <input
                      ref={inputRef}
                      disabled={disabled}
                      value={getColorValue}
                      onChange={onInputChange}
                      onFocus={(event) => {
                        event.target.select();
                        setComboboxOpen(true);
                        setFocused(true);

                        if (onInputFocus) {
                          onInputFocus(event);
                        }
                      }}
                      onKeyDown={handleKeyDown}
                      onBlur={() => {
                        setFocused(false);
                      }}
                      className={`text-2xs px-5 border-none outline-none font-medium w-full h-full rounded-md bg-secondary-n-950 ${
                        disabled || value?.set == false
                          ? "text-secondary-600"
                          : "text-white"
                      }`}
                    />

                    <button
                      onClick={() => setDropdownVisible(true)}
                      className="absolute right-1"
                    >
                      <ChevronDown className="text-secondary-n-500 h-4 w-4" />
                    </button>

                    <DropdownInput
                      className="w-40"
                      dropdownOptions={enhancedDropdownOptions}
                      onDropdownSelected={handleDropdownOptionClick}
                      // @ts-expect-error TS2322
                      visible={dropdownVisible}
                      setVisible={setDropdownVisible}
                    />
                  </div>
                  {comboboxOpen && colorProperties && (
                    <ComboBox
                      input={getColorValue}
                      options={colorProperties}
                      open={comboboxOpen}
                      setOpen={setComboboxOpen}
                      onOptionSelected={(option: ComboBoxOptionType) =>
                        handleComboboxOptionClick(option)
                      }
                      buildOptionPreview={buildColorPreview}
                      buildArbitraryClass={buildArbitraryClass}
                      anchorRef={colorInputRef}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="col-span-6">
              <DraggableInput
                value={`${positionValueForInput}%`}
                evaluatedValue={`${positionValueForInput}%`}
                setValue={(position) => {
                  const key = title.toLowerCase();
                  setPositionRange(Number(position.replace(/%/g, "")));
                  setGradient((prev) => {
                    return {
                      ...prev,
                      [key]: {
                        ...prev[key],
                        position: {
                          value: position,
                          classname: `${key}-[${position}]`,
                        },
                        set: true,
                      },
                    };
                  });
                }}
                labelContent={<></>}
                comboboxOptions={positionProperties}
                onComboboxSelected={(option) => {
                  addTailwindClass(option);
                }}
                onSliderValueUpdate={(option) => {
                  // @ts-expect-error TS2722
                  onValueUpdate(option);
                }}
                buildArbitraryClass={(input: string) => {
                  return buildPositionArbitraryClass(
                    title.toLowerCase(),
                    input
                  );
                }}
                set={true}
                setSet={(set) => {
                  const key = title.toLowerCase();
                  setGradient((prev) => {
                    return {
                      ...prev,
                      key: {
                        ...prev[key],
                        set: set,
                      },
                    };
                  });
                }}
                min={0}
                max={100}
                step={1}
                fallback={{ number: 100, unit: "%" }}
                tooltipContent={`${title} stop position`}
                onClear={() => {
                  if (value?.position?.classname) {
                    removeTailwindClass(value?.position?.classname);
                  }
                }}
              />
            </div>
            <div className="col-span-4">
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={positionValueForInput / 100}
                onChange={handlePositionRangeChange}
                onMouseDown={handlePositionRangeMouseDown}
                onMouseUp={handlePositionRangeMouseUp}
                onTouchStart={handlePositionRangeMouseDown}
                onTouchEnd={handlePositionRangeMouseUp}
                className={`w-full appearance-none bg-transparent
                      [&::-webkit-slider-runnable-track]:w-full
                      [&::-webkit-slider-runnable-track]:h-3
                      [&::-webkit-slider-runnable-track]:bg-primary-0
                      [&::-webkit-slider-runnable-track]:rounded-full
                      [&::-webkit-slider-thumb]:w-3
                      [&::-webkit-slider-thumb]:h-3
                      [&::-webkit-slider-thumb]:appearance-none
                      [&::-webkit-slider-thumb]:bg-primary-n-50
                      [&::-webkit-slider-thumb]:rounded-full
                      [&::-webkit-slider-thumb]:transition-all
                      [&::-webkit-slider-thumb]:duration-150
                      [&::-webkit-slider-thumb]:ease-in-out
                      [&::-moz-range-thumb]:w-3
                      [&::-moz-range-thumb]:h-3
                      [&::-moz-range-thumb]:appearance-none
                      [&::-moz-range-thumb]:bg-primary-n-50
                      [&::-moz-range-thumb]:border-0
                      [&::-moz-range-thumb]:border-transparent
                      [&::-moz-range-thumb]:rounded-full
                      [&::-moz-range-thumb]:transition-all
                      [&::-moz-range-thumb]:duration-150
                      [&::-moz-range-thumb]:ease-in-out
                      [&::-moz-range-track]:w-full
                      [&::-moz-range-track]:h-3
                      [&::-moz-range-track]:bg-primary-0
                      [&::-moz-range-track]:rounded-full
                    `}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const GradientInputMemo = React.memo(GradientInput);

export default React.memo(CustomGradientInput);
